import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { assetUrl } from '../../../Shared/helpers';
import { Event, Pagination, Project } from '../../../Shared/types';
import { Card } from '../../../Shared/UI/Card';
import { Pager } from '../../../Shared/UI/Pager';
import { SearchFilterInput } from '../../../Shared/UI/SearchFilterInput';
import { SharedProps } from '../../types';
import Container from '../../UI/Container';
import Header from '../../UI/Header';
import HeaderTitle from '../../UI/HeaderTitle';
import Layout from '../../UI/Layout';
import { SettingsLayout } from '../SettingsLayout';
import EventCard from './EventCard';

export interface EventsPageProps extends SharedProps {
  events: Pagination<Event & { project: Project | null; }>;
}

export default function EventsPage({
  events,
  params,
}: EventsPageProps) {
  const { t } = useTranslation();

  const containerSize = params.search || events.meta.total > 0 ? 'lg' : 'md';
  const showSearch = params.search || events.meta.total > 0;

  return (
    <>
      <Header>
        <Container size={containerSize}>
          <div className="flex items-center justify-between">
            <div>
              <HeaderTitle>
                {t('backend:events')}
              </HeaderTitle>
              {t('backend:events_description')}
            </div>
            <a
              href="https://atleta.cc"
              className="flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-600 whitespace-nowrap"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>
                {t('backend:powered_by')}
              </span>
              <img
                src={assetUrl('images/logo-atleta.svg')}
                alt="Atleta"
                className="h-4 mb-0.5"
              />
            </a>
          </div>
        </Container>
      </Header>

      <Container size={containerSize} className="space-y-6">
        {showSearch && (
          <div className="w-96">
            <SearchFilterInput id="search" />
          </div>
        )}

        {events.data.length > 0 && (
          <div className="space-y-2">
            {events.data.map((event) => (
              <EventCard event={event} key={event.id} />
            ))}
          </div>
        )}

        {events.meta.last_page > 1 && (
          <Pager data={events} />
        )}

        {!!params.search && events.meta.total === 0 && (
          <Card className="text-center text-slate-500 py-24">
            <img
              src={assetUrl('images/no-results.svg')}
              alt={t('backend:no_results')}
              width={120}
              className="block mx-auto mb-6"
            />
            {t('backend:no_results')}
          </Card>
        )}

        {!params.search && events.meta.total === 0 && (
          <Card className="text-center text-slate-500">
            <h2>
              {t('backend:no_events_yet')}
            </h2>
            {t('backend:no_events_yet_description')}
          </Card>
        )}
      </Container>
    </>
  );
}

EventsPage.layout = (page: ReactNode) => <Layout><SettingsLayout>{page}</SettingsLayout></Layout>;
